<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useSidebar,
} from '@manta/ui-kit';
import { Check, ChevronsUpDown, Building2 } from 'lucide-vue-next';
import { useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import { useMineTenantsQuery } from '@/api/tenants/useMineTenantsQuery';
import { useTenantStore } from '@/stores/tenant';

const { t } = useI18n();
const tenantStore = useTenantStore();
const queryClient = useQueryClient();
const { state } = useSidebar();

const { data: tenants, isLoading } = useMineTenantsQuery();
const selected = ref<string | undefined>(tenantStore.selectedTenantId);
const searchValue = ref('');
const isOpen = ref(false);

const activeTenants = computed(() =>
  (tenants.value ?? []).filter((tenant) => !tenant.deleted),
);
const multiple = computed(() => activeTenants.value.length > 1);

const filteredTenants = computed(() => {
  const q = searchValue.value.trim().toLowerCase();
  if (!q) return activeTenants.value;
  return activeTenants.value.filter((tenant) =>
    tenant.name.toLowerCase().includes(q),
  );
});

const selectedTenantName = computed(() => {
  if (!selected.value) return '';
  return activeTenants.value.find((t) => t.id === selected.value)?.name ?? '';
});

function selectTenant(tenantId: string) {
  selected.value = tenantId;
  tenantStore.setSelectedTenant(tenantId);
  // Refetch everything so data reflects the newly active tenant.
  queryClient.invalidateQueries();
  searchValue.value = '';
  isOpen.value = false;
}

watch(
  () => tenantStore.selectedTenantId,
  (id) => {
    selected.value = id;
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="state === 'expanded'" class="px-1">
    <Popover v-if="multiple" v-model:open="isOpen">
      <PopoverTrigger as-child>
        <Button
          variant="outline"
          role="combobox"
          :aria-expanded="isOpen"
          class="w-full justify-between"
        >
          <span class="truncate">
            {{
              selectedTenantName ||
              (isLoading ? t('common.loading') : t('tenant.select'))
            }}
          </span>
          <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        class="w-[--reka-popover-trigger-width] p-0"
        align="start"
      >
        <div class="p-2">
          <Input
            v-model="searchValue"
            :placeholder="t('tenant.search')"
            class="mb-2"
            autofocus
          />
        </div>
        <div class="max-h-64 overflow-y-auto p-1">
          <p
            v-if="filteredTenants.length === 0"
            class="p-2 text-sm text-muted-foreground"
          >
            {{ t('tenant.none') }}
          </p>
          <Button
            v-for="tenant in filteredTenants"
            :key="tenant.id"
            variant="ghost"
            class="w-full justify-start font-normal"
            @click="selectTenant(tenant.id)"
          >
            <Check
              :class="[
                'mr-2 h-4 w-4',
                selected === tenant.id ? 'opacity-100' : 'opacity-0',
              ]"
            />
            <span class="truncate">{{ tenant.name }}</span>
          </Button>
        </div>
      </PopoverContent>
    </Popover>

    <div
      v-else-if="selectedTenantName || activeTenants[0]?.name"
      class="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground"
    >
      <Building2 class="h-4 w-4 shrink-0" />
      <span class="truncate">{{
        selectedTenantName || activeTenants[0]?.name
      }}</span>
    </div>
  </div>
</template>
